import React, { useState, useEffect, useCallback } from 'react';
import { Drawer, Table, Button, Popconfirm, message, Spin, Tooltip, Tag, Typography, Collapse, Space } from 'antd'; // Added Space
import { EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import flowsheetService from '../services/flowsheetService';
import EditFlowsheetEntryForm from './EditFlowsheetEntryForm';
import './FlowsheetDataManagementPanel.css';

const { Text } = Typography;
const { Panel } = Collapse;

/**
 * Custom hook to handle responsive drawer width
 * Prevents infinite re-renders by properly managing window resize events
 */
const useResponsiveDrawerWidth = () => {
  const [drawerWidth, setDrawerWidth] = useState(() => {
    if (typeof window === 'undefined') return 1200;
    return window.innerWidth > 1200 ? 1200 : '95%';
  });

  useEffect(() => {
    const handleResize = () => {
      setDrawerWidth(window.innerWidth > 1200 ? 1200 : '95%');
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return drawerWidth;
};

const FlowsheetDataManagementPanel = ({ visible, onClose, patientId }) => {
  const drawerWidth = useResponsiveDrawerWidth();
  const [loading, setLoading] = useState(false);
  const [groupedFlowsheetData, setGroupedFlowsheetData] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [activeCollapseKeys, setActiveCollapseKeys] = useState([]);

  const fetchData = useCallback(async () => {
    if (!patientId || !visible) {
      return;
    }
    setLoading(true);
    try {
      const response = await flowsheetService.getFlowsheetData(patientId);
      const rawData = response.data.flowsheetData || [];

      const groups = rawData.reduce((acc, item) => {
        const dateKey = moment(item.date).toISOString(); // Use ISO string as a consistent key
        if (!acc[dateKey]) {
          acc[dateKey] = {
            timestamp: dateKey,
            dateDisplay: moment(item.date).format('YYYY-MM-DD HH:mm:ss'),
            entries: [],
            entryTypesSet: new Set(), // Initialize a Set for unique types
          };
        }
        acc[dateKey].entries.push({
          ...item,
          key: item.entryId || `temp-key-${item.name}-${Math.random().toString(36).substring(2, 11)}`,
        });
        if (item.type) {
          acc[dateKey].entryTypesSet.add(item.type);
        }
        return acc;
      }, {});

      const groupedArray = Object.values(groups).map(group => ({
        ...group,
        entryTypes: Array.from(group.entryTypesSet).sort(), // Convert Set to sorted array
      })).sort((a, b) => moment(b.timestamp).diff(moment(a.timestamp))); // Sort by most recent first
      
      setGroupedFlowsheetData(groupedArray);
      // Automatically expand the first panel (most recent timestamp)
      if (groupedArray.length > 0) {
        setActiveCollapseKeys([groupedArray[0].timestamp]);
      } else {
        setActiveCollapseKeys([]);
      }

    } catch (error) {
      message.error('Failed to load flowsheet data for management.');
      console.error('Error fetching flowsheet data for management:', error);
      setGroupedFlowsheetData([]);
    } finally {
      setLoading(false);
    }
  }, [patientId, visible]);

  useEffect(() => {
    if (visible && patientId) {
      fetchData();
    } else if (!visible) {
      setGroupedFlowsheetData([]);
      setActiveCollapseKeys([]);
    }
  }, [visible, patientId, fetchData]);

  const handleEdit = async (entry) => {
    // If entry doesn't have entryId, promote it to FlowsheetEntry first
    if (!entry.entryId) {
      try {
        setLoading(true);
        const promoteData = {
          date: entry.date,
          type: entry.type,
          name: entry.name,
          value: entry.value,
          units: entry.units,
          category: entry.category,
          source: entry.source,
          referenceRange: entry.referenceRange,
          flag: entry.flag,
          structuredValue: entry.structuredValue
        };

        const response = await flowsheetService.promoteToFlowsheetEntry(patientId, promoteData);
        message.success('Entry promoted to editable format successfully!');

        // Update the entry with the new entryId and set it for editing
        const promotedEntry = {
          ...entry,
          entryId: response.data.entry._id,
          source: response.data.entry.source
        };
        setEditingEntry(promotedEntry);
        setIsEditModalVisible(true);

        // Refresh data to show the promoted entry
        fetchData();
      } catch (error) {
        console.error('Error promoting entry:', error);
        if (error.message && error.message.includes('already been promoted')) {
          // If the entry was already promoted, we can get the entryId from the error response
          if (error.data && error.data.entryId) {
            message.info('Entry was already promoted. Opening for editing...');
            const promotedEntry = {
              ...entry,
              entryId: error.data.entryId,
              source: `Promoted from ${entry.source}`
            };
            setEditingEntry(promotedEntry);
            setIsEditModalVisible(true);
          } else {
            message.warning('This entry has already been promoted. Please refresh the data and try again.');
            fetchData();
          }
        } else {
          message.error(error.message || 'Failed to promote entry for editing.');
        }
      } finally {
        setLoading(false);
      }
    } else {
      // Entry already has entryId, can edit directly
      setEditingEntry(entry);
      setIsEditModalVisible(true);
    }
  };

  const handleDelete = async (entryId) => {
    if (!entryId) {
      message.error('Entry ID is missing, cannot delete.');
      return;
    }
    setLoading(true);
    try {
      await flowsheetService.archiveFlowsheetEntry(patientId, entryId);
      message.success('Flowsheet entry archived successfully!');
      fetchData(); // Refresh data
    } catch (error) {
      message.error(error.message || 'Failed to archive flowsheet entry.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async (timestamp, entryCount) => {
    if (!timestamp) {
      message.error('Timestamp is missing, cannot delete entries.');
      return;
    }
    setLoading(true);
    try {
      await flowsheetService.bulkArchiveFlowsheetEntriesByTimestamp(patientId, timestamp);
      message.success(`Successfully archived ${entryCount} flowsheet entries for the selected timestamp!`);
      fetchData(); // Refresh data
    } catch (error) {
      message.error(error.message || 'Failed to bulk archive flowsheet entries.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditModalOk = () => {
    setIsEditModalVisible(false);
    setEditingEntry(null);
    fetchData(); 
  };

  const handleEditModalCancel = () => {
    setIsEditModalVisible(false);
    setEditingEntry(null);
  };

  const renderValue = (value) => {
    if (typeof value === 'object' && value !== null) {
      const jsonString = JSON.stringify(value, null, 2);
      return <Text ellipsis={{ tooltip: <pre style={{maxWidth: '400px', whiteSpace: 'pre-wrap'}}>{jsonString}</pre> }}>{jsonString.substring(0,100)}{jsonString.length > 100 ? '...' : ''}</Text>;
    }
    if (typeof value === 'string' && value.length > 100) {
        return <Text ellipsis={{ tooltip: value }}>{value.substring(0, 100)}...</Text>;
    }
    if (typeof value === 'boolean') {
        return <Tag color={value ? 'green' : 'red'}>{String(value)}</Tag>;
    }
    return String(value);
  };

  const innerTableColumns = [
    { title: 'Type', dataIndex: 'type', key: 'type', width: 120, ellipsis: true },
    { title: 'Name', dataIndex: 'name', key: 'name', width: 180, ellipsis: true },
    { title: 'Value', dataIndex: 'value', key: 'value', render: renderValue, ellipsis: true, width: 200 },
    { title: 'Units', dataIndex: 'units', key: 'units', width: 80, ellipsis: true },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 160,
      ellipsis: true,
      render: (source, record) => (
        <Tooltip title={`${source}${record.entryId ? ' ✓ (Editable)' : ' (will promote to edit)'}`}>
          <span style={{ color: record.entryId ? '#52c41a' : '#1890ff' }}>
            {source}
            {record.entryId ? ' ✓' : ' (promote)'}
          </span>
        </Tooltip>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" style={{ display: 'flex', justifyContent: 'center' }}>
          <Tooltip title={record.entryId ? "Edit Entry" : "Edit Entry (will promote to flowsheet)"}>
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
              loading={loading}
              style={{ minWidth: '32px' }}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to archive this entry?"
            onConfirm={() => handleDelete(record.entryId)}
            okText="Yes, Archive"
            cancelText="No"
            disabled={!record.entryId}
          >
            <Tooltip title={record.entryId ? "Archive Entry" : "Cannot archive - entry from encounter/intake/order"}>
              <Button
                icon={<DeleteOutlined />}
                danger
                size="small"
                disabled={!record.entryId}
                style={{ minWidth: '32px' }}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Drawer
        title="Manage Flowsheet Data"
        width={drawerWidth}
        onClose={onClose}
        open={visible}
        styles={{ body: { padding: '16px' } }}
        extra={
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
            Refresh Data
          </Button>
        }
        destroyOnClose // Ensures state is reset when drawer is closed and reopened
        className="flowsheet-management-panel"
      >
        <Spin spinning={loading}>
          {groupedFlowsheetData.length === 0 && !loading ? (
            <Text>No data available for the selected criteria.</Text>
          ) : (
            <Collapse
              activeKey={activeCollapseKeys}
              onChange={(keys) => setActiveCollapseKeys(keys)}
              className="flowsheet-management-panel"
            >
              {groupedFlowsheetData.map(group => (
                <Panel
                  header={
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      minHeight: '32px'
                    }}>
                      <span style={{
                        flex: 1,
                        marginRight: '12px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        <Tooltip title={`Timestamp: ${group.dateDisplay} - Types: ${group.entryTypes.join(', ') || 'N/A'} (${group.entries.length} entries)`}>
                          {`${group.dateDisplay} - ${group.entryTypes.join(', ') || 'N/A'} (${group.entries.length})`}
                        </Tooltip>
                      </span>
                      <Popconfirm
                        title={`Delete all ${group.entries.length} entries for this timestamp?`}
                        description="This action will archive all flowsheet entries for this timestamp. This cannot be undone."
                        onConfirm={(e) => {
                          e.stopPropagation(); // Prevent collapse toggle
                          handleBulkDelete(group.timestamp, group.entries.length);
                        }}
                        onCancel={(e) => e.stopPropagation()} // Prevent collapse toggle
                        okText="Yes, Delete All"
                        cancelText="Cancel"
                        okType="danger"
                      >
                        <Tooltip title="Delete all entries for this timestamp">
                          <Button
                            icon={<DeleteOutlined />}
                            danger
                            size="small"
                            onClick={(e) => e.stopPropagation()} // Prevent collapse toggle
                            style={{ flexShrink: 0 }}
                          />
                        </Tooltip>
                      </Popconfirm>
                    </div>
                  }
                  key={group.timestamp}
                >
                  <Table
                    columns={innerTableColumns}
                    dataSource={group.entries}
                    rowKey="key"
                    pagination={false} // Pagination per group might be too much, consider for overall if needed
                    scroll={{ x: 1000 }}
                    size="small"
                    bordered
                  />
                </Panel>
              ))}
            </Collapse>
          )}
        </Spin>
      </Drawer>
      {editingEntry && patientId && (
        <EditFlowsheetEntryForm
          visible={isEditModalVisible}
          onCancel={handleEditModalCancel}
          onOk={handleEditModalOk}
          entry={editingEntry}
          patientId={patientId}
        />
      )}
    </>
  );
};

export default FlowsheetDataManagementPanel;
